import { Clock, Phone, Mail, MessageCircle } from "lucide-react";
import { STORE, telLink, waLink } from "@/lib/store";

export default function StoreInfo() {
    return (
        <section id="contact" data-testid="store-info-section" className="bg-charcoal text-white py-24 md:py-32 relative overflow-hidden">
            <div className="absolute inset-0 bg-blueprint opacity-70 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                    <div className="lg:col-span-6">
                        <p className="uppercase tracking-[0.3em] text-xs font-semibold text-safety mb-4">/03 · Get in touch</p>
                        <h2 className="font-display font-extrabold tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                            Electrical & plumbing, delivered.
                        </h2>
                        <p className="mt-6 text-white/70 leading-relaxed max-w-md">
                            No walk-in store — WhatsApp or call us with your wire, switch, pipe
                            or fitting list. We'll confirm availability, price and deliver across{" "}
                            {STORE.serviceArea}.
                        </p>

                        <div className="mt-10 space-y-6">
                            <InfoRow icon={Phone} title="Phone">
                                <a href={telLink()} className="hover:text-safety transition-colors">{STORE.phoneDisplay}</a>
                            </InfoRow>
                            <InfoRow icon={MessageCircle} title="WhatsApp">
                                <a href={waLink()} target="_blank" rel="noreferrer" className="hover:text-safety transition-colors">
                                    Chat for a quick quote
                                </a>
                            </InfoRow>
                            <InfoRow icon={Mail} title="Email">
                                <a href={`mailto:${STORE.email}`} className="break-all hover:text-safety transition-colors">
                                    {STORE.email}
                                </a>
                            </InfoRow>
                            <InfoRow icon={Clock} title="Support hours">
                                {STORE.hours.map((h) => (
                                    <p key={h.label}>
                                        <span className="text-white font-semibold">{h.label}:</span> {h.value}
                                    </p>
                                ))}
                            </InfoRow>
                        </div>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <a
                                href={waLink()}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="store-info-whatsapp-btn"
                                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-safety hover:bg-safety-hover text-white font-semibold btn-shine"
                            >
                                <MessageCircle className="w-4 h-4" /> WhatsApp us
                            </a>
                            <a
                                href={telLink()}
                                data-testid="store-info-call-btn"
                                className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-white/20 hover:bg-white/10 font-semibold"
                            >
                                <Phone className="w-4 h-4" /> Call now
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-6">
                        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 md:p-10 shadow-2xl">
                            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-safety">How it works</p>
                            <ol className="mt-6 space-y-5 text-white/80 text-sm leading-relaxed">
                                <li className="flex gap-4">
                                    <span className="font-display font-bold text-safety text-lg">1</span>
                                    <span>Send your list — wire gauge, switch type, pipe size, brand or quantity.</span>
                                </li>
                                <li className="flex gap-4">
                                    <span className="font-display font-bold text-safety text-lg">2</span>
                                    <span>We confirm stock and price on WhatsApp or call.</span>
                                </li>
                                <li className="flex gap-4">
                                    <span className="font-display font-bold text-safety text-lg">3</span>
                                    <span>We arrange delivery across {STORE.serviceArea}.</span>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function InfoRow({ icon: Icon, title, children }) {
    return (
        <div className="flex items-start gap-4">
            <div className="mt-0.5 w-10 h-10 rounded-lg bg-white/5 border border-white/10 grid place-items-center">
                <Icon className="w-5 h-5 text-safety" />
            </div>
            <div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50">{title}</p>
                <div className="mt-1 text-white/80 text-sm leading-relaxed">{children}</div>
            </div>
        </div>
    );
}
